/**
 * Keep the one server-rendered Product JSON-LD aligned with the main product's selected variant.
 * Liquid owns price, currency, availability and Judge.me ratings; this module never calculates them.
 * Pending/failed/unavailable combinations drop the old Offer but retain the Product and its reviews.
 */
import { StandardEvents } from '@shopify/events';

const TAG = 'ayunel-product-jsonld';
const shortId = (id) => String(id ?? '').split('/').pop();

class AyunelProductJsonld extends HTMLElement {
  #controller;
  #generation = 0;

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.#scope()?.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });
    window.addEventListener('pageshow', this.#verifyAgainstPage, { signal });
    this.#verifyAgainstPage();
  }

  disconnectedCallback() {
    this.#generation += 1;
    this.#controller?.abort();
  }

  #scope() {
    return this.closest('.shopify-section');
  }

  get #script() {
    return this.querySelector('script[type="application/ld+json"]');
  }

  #verifyAgainstPage = () => {
    const input = this.#scope()?.querySelector('product-form-component input[name="id"], form[action*="/cart/add"] input[name="id"]');
    // A late module or restored page must not present an Offer belonging to an unresolved/old selection.
    if (input?.value && String(input.value) === this.dataset.variantId) return;
    this.#generation += 1;
    this.#removeOffer();
  };

  #removeOffer() {
    const script = this.#script;
    if (!script) return;
    try {
      const data = JSON.parse(script.textContent || '{}');
      delete data.offers;
      script.textContent = JSON.stringify(data);
      this.dataset.variantId = '';
    } catch {
      // Do not leave malformed structured data in the page.
      script.textContent = '';
    }
  }

  #onProductSelect = (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('product-card') || event.target.closest('quick-add-dialog')) return;
    if (event.target.closest('.shopify-section') !== this.#scope()) return;
    if (shortId(event.product?.id) !== this.dataset.productId || !event.promise?.then) return;

    const generation = ++this.#generation;
    this.#removeOffer();
    event.promise.then(({ detail } = {}) => {
      if (generation !== this.#generation || !detail?.html || !detail.resource) return;
      if (shortId(detail.productId) !== this.dataset.productId) return;
      const variantId = shortId(detail.resource.id);
      const next = detail.html.querySelector(
        `${TAG}[data-product-id="${CSS.escape(this.dataset.productId || '')}"][data-section-id="${CSS.escape(this.dataset.sectionId || '')}"]`
      );
      if (!next || next.dataset.variantId !== variantId) return;
      const nextScript = next.querySelector('script[type="application/ld+json"]');
      const script = this.#script;
      if (!script || !nextScript) return;
      const data = JSON.parse(nextScript.textContent || '{}');
      const current = JSON.parse(script.textContent || '{}');
      if (data['@type'] !== 'Product' || data['@id'] !== current['@id']) return;
      if (data.offers?.['@type'] !== 'Offer' || new URL(data.offers.url).searchParams.get('variant') !== variantId) return;
      // Update the existing script in place: no second Product, no client-side price or review fabrication.
      script.textContent = nextScript.textContent;
      this.dataset.variantId = variantId;
    }).catch(() => { /* Rejected or malformed responses keep the old Offer absent. */ });
  };
}

if (!customElements.get(TAG)) customElements.define(TAG, AyunelProductJsonld);
